import React, { useState, useEffect, forwardRef } from 'react';
import { Typography, ButtonGroup, Button, FormControl, InputLabel, Select, MenuItem, Icon } from '@mui/material';
import useStyles from './styles';
import { get, set, size, flatten, unset, uniq } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import clsx from 'clsx';
import { Charting } from 'global/components/Charting';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { useScreen } from 'global/styles';
import { useUser } from '../../../../global/hooks/useUser';
import { Row } from '../../../../global/components/row';
import { MoreList } from '../../../../global/components/more-drop';
import SnackAlerts from '../../../../global/components/snack-alerts';
import chartStyles from '../../../../global/components/Charting/styles';
import { useChartSettings } from '../../../../global/hooks/useChartSettings';

// ##filters
// assessment_id,
// scorecard_id,
// superset_id,
// checklist_id,
// checkpoint_id,
// realm_id*,
// realm_type**,
// depth,
// start_date*,
// end_date*

export const getRowDefs = (row) => {
  const defs = {
    scorecard: {
      parent: undefined,
      rowSelect: {
        rows: 'superset',
      },
    },
    superset: {
      parent: 'scorecard_header',
      rowSelect: {
        rows: 'checklist',
      },
    },
    checklist: {
      parent: 'superset',
      rowSelect: {
        rows: 'checkpoint',
      },
    },
    checkpoint: {
      parent: 'checklist',
    },
  };

  return get(defs, row);
};

export default forwardRef((props, refs) => {
  const { chartSettings, savedCharts, showSavedCharts } = props;
  const classes = useStyles();
  const cStyles = chartStyles();
  const filter = useSelector((state) => get(state, 'filter.reports.data'));
  const { isMobile } = useScreen();
  const [chips, setChips] = useState({});
  const { me, siteContextSelectedRealm } = useUser();
  const [alerts, setAlerts] = useState();

  const [state, setState] = useState({
    load: 1,
    userGroups: get(me, 'userGroups', []),
    selected: {
      rows: null,
      columns: null,
    },
  });

  const [dstate, setdstate] = useState({
    data: [],
    headerRow: [],
    loading: false,
  })

  const realmDisableCheck = (_state, compareKey = 'rows') => {
    const rowSelected = get(_state, `selected.${compareKey}`);
    const disabled = ['builder', 'division', 'community', 'lot'];

    if (disabled.indexOf(rowSelected) > -1) {
      return true;
    }
    return false;
  };

  const getColKey = (p) => {
    return get(p, 'selectedRow.tableBy') || 'superset';
  };

  const [content] = useState({
    KEYS: ['rows', 'columns'],
    rows: {
      title: 'Row',
      buttonGroups: [
        [
          { children: 'Scorecard', id: 'scorecard', tier: 'scorecard', showColAvg: true, onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'superset' } })) },
          {
            children: 'System',
            id: 'superset',
            parent: 'scorecard_header',
            tableBy: 'edition',
            tier: 'scorecard',
            showColAvg: true,
            onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'checklist' } })),
          },
          {
            children: 'Checklist',
            id: 'checklist',
            parent: 'superset',
            tableBy: 'superset',
            tier: 'superset',
            showColAvg: true,
            onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'checkpoint' } })),
          },
          { children: 'Checkpoint', id: 'checkpoint', parent: 'checklist', tableBy: 'checklist', showColAvg: true, isDisabled: () => true },
        ],
        [
          { children: 'Builder', id: 'builder', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns'), isVisible: () => !!size(props.vendors) },
          { children: 'Division', id: 'division', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns') },
          { children: 'Community', id: 'community', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns') },
          { children: 'Lot', id: 'lot', groupBy: 'realm', showColAvg: true, tableBy: 'edition', isDisabled: (s) => realmDisableCheck(s, 'columns') },
        ],
      ],
    },
    columns: {
      title: 'Column',
      buttonGroups: [
        [
          { children: 'Builder', id: 'builder', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows'), isVisible: () => !!size(props.vendors) },
          { children: 'Division', id: 'division', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Community', id: 'community', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Lot', id: 'lot', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
        ],
        [
          { children: 'Yearly', id: 'yearly', colKey: getColKey, groupBy: 'relationships.year.id' },
          { children: 'Quarterly', id: 'quarterly', colKey: getColKey, groupBy: 'relationships.quarter.id' },
          { children: 'Monthly', id: 'monthly', colKey: getColKey, groupBy: 'relationships.month.id' },
        ],
        [
          { children: 'Assessment', id: 'assessments', groupBy: 'assessment', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Company', id: 'company', groupBy: 'vendor', colKey: getColKey, isVisible: () => !!size(props.vendors) },
          { children: 'Author', id: 'author', groupBy: 'author', colKey: getColKey },
          { children: 'Assignee', id: 'assignee', groupBy: 'assignee', colKey: getColKey },
          { children: 'Template', id: 'template', groupBy: 'template', colKey: getColKey },
        ],
      ],
    },
  });

  // let [opts, setOpts] = useState({
  //   build: ['dataReport'],
  //   waitFor: null,
  //   useEffect: true,
  //   useNamespace: false,
  //   returnRaw: true,
  //   params: {},
  //   transformData: TransformData,
  // });

  // let [opts2, setOpts2] = useState(cleanCopy(opts));

  // const { data, loading } = useRetrieveData({ url: '/data-reports' }, opts);
  // const headerRow = useRetrieveData({ url: '/data-reports' }, opts2);
  const usergroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'], useCache: { reload: 60 } });

  const chartSet = useChartSettings({ loadCharts: false, loadRealmCharts: false })

  const canLoad = !!(get(state, 'selected.rows') && get(state, 'selected.columns'));

  const getChipFilters = (cps = chips) => {
    const obj = {};
    Object.keys(cps).map((k) => {
      set(obj, k, get(cps, [k, 'id']));
    });
    return obj;
  };

  const getSelectedRow = (sel = 'rows', item = state) => {
    return flatten(get(content, `${sel}.buttonGroups`, [])).find((r) => r.id === get(item, `selected.${sel}`));
  };

  useEffect(() => {
    props.onFilters && props.onFilters(chips);
  }, [chips]);

  useEffect(() => {
    props.updateUserGroups && props.updateUserGroups(state.userGroups);
  }, [state.userGroups]);

  const loadCharts = async (chart, filter, row) => {
    try {
      setdstate(s => ({ ...s, data: [], loading: true }));
      const d = await chartSet.loadChartData(chart, filter, row);
      setdstate(ds => ({ ...ds, ...d }));
    } catch (err) {
      console.error(err);
    } finally {
      setdstate(s => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    if (canLoad) {
      // const _opts = { ...opts, waitFor: undefined };

      const filters = buildFilters(filter, {
        dontFilter: ['ownership', 'quality_type', 'superset_id', 'user_id', 'daterange'],
        dontSort: true,
        overrideFilter: (f) => {
          if (f.realms) {
            f.realm_id = uniq([f.realm_id, ...f.realms.split(',')].filter((f) => !!f)).join(',');
          }

          return {
            ...f,
            realms: undefined,
          };
        },
      });

      if (get(filters, 'filter.scorecard_id') === 0) {
        set(filters, 'filter.scorecard_id', undefined);
      }

      const chipfilts = getChipFilters();

      const scorecard_id = get(chipfilts, 'scorecard_id', get(filters, 'filter.scorecard_id'));

      const opts = {
        filter: {
          ...chipfilts,
          ...filters.filter,
          scorecard_id,
          //if the scorecard is set, the edition_id filter is redundant, and sometimes problematic
          edition_id: scorecard_id ? undefined : get(chipfilts, 'edition_id', get(filters, 'filter.edition_id', get(filters, 'filter.scorecard_id', get(chipfilts, 'scorecard_id')))),
          _showCompare: filter._showCompare,
        },
        meta: state.selected
      }

      // set(_opts, 'params.filter', {
      //   ...chipfilts,
      //   ...filters.filter,
      //   scorecard_id,
      //   //if the scorecard is set, the edition_id filter is redundant, and sometimes problematic
      //   edition_id: scorecard_id ? undefined : get(chipfilts, 'edition_id', get(filters, 'filter.edition_id', get(filters, 'filter.scorecard_id', get(chipfilts, 'scorecard_id')))),
      // });
      // set(_opts, 'params.meta', state.selected);

      const key = 'filter.edition_id';
      if (get(opts, key) == 0) {
        unset(opts, key);
      }

      // setOpts({ ..._opts });

      // const _opts2 = cleanCopy(_opts);
      // const opt2row = get(getSelectedRow(), 'parent', '');
      // set(_opts2, 'params.meta.rows', opt2row);
      // set(_opts2, 'transformData', TransformData);
      // setOpts2(_opts2);

      loadCharts({ config: { params: opts } }, opts.filter);
    }
  }, [state, filter, siteContextSelectedRealm, chips]);

  const onClick = (key, btn) => {
    const sel = `selected.${key}`;
    const isSelected = get(state, sel) === btn.id;
    set(state, sel, isSelected ? null : btn.id);
    setState({ ...state });
  };

  const onClickMobile = (key, btn) => {
    const sel = `selected.${key}`;
    const isSelected = get(state, sel) === btn.target.value;
    set(state, sel, isSelected ? null : btn.target.value);
    setState({ ...state });
  };

  const onRowSelect = (filt) => {
    setChips((c) => ({ ...c, ...filt }));
    get(getSelectedRow(), 'onRowSelect', () => 0)();
  };

  const removeFilter = (k) => {
    setChips({
      ...chips,
      [k]: undefined,
    });
  };

  const clickUg = (ug) => {
    const has = state.userGroups.find((u) => u.id == ug.id);
    setState((s) => ({ ...s, userGroups: !!has ? state.userGroups.filter((u) => u.id != ug.id) : [...state.userGroups, ug] }));
  };

  set(refs, 'current.removeFilter', removeFilter);

  return (
    <div className={classes.container}>
      {!showSavedCharts && (
        <div className={classes.filters}>
          {content.KEYS.map((k) => {
            if (!isMobile) {
              return (
                <div key={k} className={classes.filterContainer}>
                  <Typography variant="subtitle1" className={classes.overline}>
                    {get(content, `${k}.title`)}
                  </Typography>

                  {get(content, `${k}.buttonGroups`, []).map((btnGroup, i) => {
                    return (
                      <ButtonGroup key={i} className={classes.buttonGroup} orientation="vertical" variant="contained">
                        {btnGroup.map((btn, _i) => {
                          const { isDisabled, isVisible, columnBy, onRowSelect, tableBy, groupBy, colKey, loading, ...rest } = btn;
                          const disabled = (isDisabled || (() => false))(state);
                          const visible = (isVisible || (() => true))();
                          const active = get(state, `selected.${k}`) === btn.id;
                          return !visible ? null : <Button key={_i} {...rest} disabled={active ? false : disabled} className={clsx(active && classes.buttonActive)} onClick={() => onClick(k, btn)} />;
                        })}
                      </ButtonGroup>
                    );
                  })}
                </div>
              );
            } else {
              return (
                <FormControl key={k}>
                  <InputLabel>{get(content, `${k}.title`)}</InputLabel>
                  <Select
                    value={get(state, `selected.${k}`)}
                    size="small"
                    onChange={(e) => {
                      onClickMobile(k, e);
                    }}
                    variant="standard"
                  >
                    <MenuItem key={`${k}-none`} value={null}>
                      None
                    </MenuItem>
                    {get(content, `${k}.buttonGroups`, []).map((btnGroup, i) =>
                      btnGroup.map((btn, _i) => {
                        const disabled = get(btn, 'isDisabled', () => false)(state);
                        const visible = get(btn, 'isVisible', () => true)();
                        const active = get(state, `selected.${k}`) === btn.id;
                        btn = {
                          ...btn,
                          value: get(btn, 'id'),
                          disabled: active ? false : disabled,
                        };
                        return !visible ? null : <MenuItem key={i} {...btn}></MenuItem>;
                      })
                    )}
                  </Select>
                </FormControl>
              );
            }
          })}
        </div>
      )}

      {showSavedCharts && (
        <Row className={classes.smallMarginTopBottom}>
          <MoreList
            type="checkbox"
            options={usergroups.data.map((u) => ({
              ...u,
              disabled: chartSettings.loadingChartData,
              onClick: () => clickUg(u),
              selected: !!state.userGroups.find((ug) => ug.id == u.id),
              title: u.name,
            }))}
            renderButton={() => (
              <Row>
                <Icon>supervised_user_circle</Icon>
                <span style={{ margin: `0 8px` }}>User Group</span>
                <Icon>arrow_drop_down</Icon>
              </Row>
            )}
            buttonProps={{ variant: 'outlined', color: 'secondary' }}
          />
        </Row>
      )}

      {showSavedCharts && !!size(savedCharts) && (
        <div className={classes.chartWrap}>
          {chartSettings.chartsWithData.map((chart, i) => {
            return (
              <div className={cStyles.chartWrapEach}>
                <Charting
                  key={chart.id}
                  {...props}
                  {...chart}
                  isSingleChart
                  getSelectedRow={getSelectedRow}
                  selectedRow={getSelectedRow('rows', { selected: { rows: chart.rows } })}
                  selectedColumn={getSelectedRow('columns', { selected: { columns: chart.columns } })}
                  chartSettings={chartSettings}
                  setAlerts={setAlerts}
                  getChipFilters={getChipFilters}
                />
              </div>
            );
          })}
        </div>
      )}

      {!showSavedCharts && (
        <Charting
          {...props}
          {...state.selected}
          {...dstate}
          showSlider={true}
          originalChartData={undefined}
          selectedRow={getSelectedRow()}
          selectedColumn={getSelectedRow('columns')}
          chips={chips}
          onRowSelect={onRowSelect}
          chartSettings={chartSettings}
          loadCharts={() => setState(s => ({ ...s, load: s.load + 1 }))}
          // opts={opts}
          setAlerts={setAlerts}
        />
      )}

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
});
